import { useToast } from '@chakra-ui/react';
import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useListPatientCasesQuery } from '@/@generated/codegen/API';
import {
  LoggedType,
  useUserAuthContext,
} from '@/components/AuthTokenContextProvider/UserAuthContext';
import { useNotInMeetingContext } from '@/features/meeting/context/CurrentMeetingDataContext';
import { useQueryParams } from '@/hooks/useQueryParams';
import { ROUTES } from '@/routes/Routes';
import { PageLoading } from '@/ui/PageLoading';

import CaseView from '../admin/CaseView';

export const QUERY_PARAM_PREVIEW_CASE_TYPE = 'previewType';

export default function PreviewCasePermissions() {
  const { patientCaseIds: patientCaseIdsUrl } = useParams();
  const patientCaseIds = patientCaseIdsUrl?.split(',') ?? [];
  const { previewType } = useQueryParams([QUERY_PARAM_PREVIEW_CASE_TYPE], null);
  const { status: userStatus } = useUserAuthContext();
  const navigate = useNavigate();
  const toast = useToast();

  const { status: meetingStatus, createAndJoinFakeMeeting } = useNotInMeetingContext();
  const patientCaseQuery = useListPatientCasesQuery({
    variables: {
      where: {
        id: {
          in: patientCaseIds,
        },
      },
    },
  });
  const hasJoinedRef = useRef<boolean>(false);

  const isLoading = patientCaseQuery.loading;

  useEffect(() => {
    if (
      patientCaseIds.length === 0 ||
      userStatus !== LoggedType.LOGGED_IN ||
      patientCaseQuery.loading ||
      patientCaseQuery.data?.listPatientCases.edges.length !== patientCaseIds.length
    )
      return;

    // NOTE: only run once
    if (hasJoinedRef.current === true) return;
    hasJoinedRef.current = true;

    if (!previewType) {
      createAndJoinFakeMeeting(patientCaseIds);
    }
  }, [
    patientCaseIds,
    userStatus,
    patientCaseQuery.data?.listPatientCases,
    previewType,
    createAndJoinFakeMeeting,
  ]);

  useEffect(() => {
    if (
      patientCaseQuery.error ||
      (!patientCaseQuery.loading &&
        patientCaseQuery.data?.listPatientCases.edges.length !== patientCaseIds.length)
    ) {
      toast({
        status: 'error',
        title: 'Some of the PatientCases do not exist or you have no access to them.',
      });
      navigate(ROUTES.HOME, { replace: true });
    }
  }, [patientCaseQuery.data, patientCaseQuery.error]);

  if (isLoading) {
    return <PageLoading />;
  }

  if (meetingStatus == 'in-meeting') {
    return <CaseView />;
  }

  return <PageLoading />;
}
